import React, { useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Users, Send, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RecruiterRoster } from "@/components/RecruiterPanel";
import { RecruitsAdmin } from "@/pages/Admin";
import { BroadcastComposer } from "@/components/Broadcast";

export default function Recruits() {
  const { effectiveRole } = useAuth();
  const [nudging, setNudging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const fileRef = useRef(null);

  if (effectiveRole !== "recruiter" && effectiveRole !== "admin") {
    return <Navigate to="/" replace />;
  }

  const sendNudges = async () => {
    setNudging(true);
    try {
      const { data } = await api.post("/admin/ship-reminders/run");
      toast.success(data.sent > 0 ? `Sent ${data.sent} ship-day nudge${data.sent === 1 ? "" : "s"}.` : "No recruits are due for a nudge right now.");
    } catch (e) {
      toast.error("Could not send nudges.");
    } finally { setNudging(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const t = toast.loading(`Reading ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/me/recruits/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} new, updated ${data.updated}${data.skipped ? `, skipped ${data.skipped}` : ""}.`, { id: t });
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed. Try a clearer file.", { id: t });
    } finally { setImporting(false); }
  };

  return (
    <div data-testid="recruits-page" className="space-y-6">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-3">
          <Users className="gold" size={26} />
          <h1 className="heading text-3xl leading-none">Recruits</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.pdf" className="hidden" onChange={onFile} data-testid="import-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-recruits-btn"
            className="px-4 py-2.5 text-sm rounded-lg border border-[var(--rsp-gold-dim)] text-[var(--rsp-gold)] hover:bg-[var(--rsp-gold)]/10 flex items-center gap-2 disabled:opacity-50 font-head uppercase tracking-wide">
            <Upload size={15} /> {importing ? "Importing…" : "Import Recruits"}
          </button>
          {effectiveRole === "admin" && (
            <button onClick={sendNudges} disabled={nudging} data-testid="send-nudges-btn"
              className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50">
              <Send size={15} /> {nudging ? "Sending…" : "Send Ship-Day Nudges Now"}
            </button>
          )}
        </div>
      </div>
      <p className="text-[var(--rsp-muted)] text-xs -mt-3">Upload a spreadsheet (CSV/XLSX) or PDF — we'll read it and add the recruits automatically.</p>

      {effectiveRole === "recruiter" && <RecruiterRoster key={reloadKey} />}
      {effectiveRole === "admin" && (
        <div className="space-y-6">
          <BroadcastComposer />
          <RecruitsAdmin key={reloadKey} />
        </div>
      )}
    </div>
  );
}
